var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { create, destroy, EVENT, } from 'zoid/dist/zoid.frameworks.frame';
import { getContainerCss } from './components/loader/css';
import { loaderComponentHtml } from './components/loader/html';
import { getCSS } from './components/modal/css';
import { getModalWrapper } from './components/modal/html';
import { once } from './utils/once';
import { adaptPluggyConnectProps, } from './utils/props';
import { getSdkVersion } from './version';
// the URL where the widget component is being hosted
const CONNECT_PRODUCTION_URL = 'https://connect.pluggy.ai';
let zoidComponentInstance;
// reference to the container uid, generated once it's rendered
let pluggyConnectContainerUid;
/**
 * Helper to wrap container class names with current container UID
 * @param className
 */
function containerCssClassName(className) {
    return `${pluggyConnectContainerUid}_${className}`;
}
/**
 * Method to be executed from both the child and parent components, to execute the
 * handshake between both so the parent can render the child.
 *
 * @returns a singleton ZoidComponent reference, that will take care of
 * wrapping and rendering the actual ZoidComponentInstance (this
 * should be instantiated only once per page/context).
 */
export function initialize() {
    if (!zoidComponentInstance) {
        zoidComponentInstance = create({
            // The html tag used to render the component
            tag: 'pluggy-connect-widget',
            // The url of the page that will show in the iframe or popup, when someone includes the component on their site
            url: ({ props }) => {
                const { url: urlByProp = CONNECT_PRODUCTION_URL } = props;
                return urlByProp;
            },
            dimensions: {
                width: '320px',
                height: '568px',
            },
            props: {
                connectToken: {
                    type: 'string',
                },
                url: {
                    type: 'string',
                    required: false,
                },
                includeSandbox: {
                    type: 'boolean',
                    required: false,
                },
                allowConnectInBackground: {
                    type: 'boolean',
                    required: false,
                },
                allowFullscreen: {
                    type: 'boolean',
                    required: false,
                },
                updateItem: {
                    type: 'string',
                    required: false,
                },
                connectorTypes: {
                    type: 'array',
                    required: false,
                },
                connectorIds: {
                    type: 'array',
                    required: false,
                },
                countries: {
                    type: 'array',
                    required: false,
                },
                selectedConnectorId: {
                    type: 'number',
                    required: false,
                },
                language: {
                    type: 'string',
                    required: false,
                },
                theme: {
                    type: 'string',
                    required: false,
                },
                moveSecurityData: {
                    type: 'string',
                    required: false,
                },
                products: {
                    type: 'array',
                    required: false,
                },
                sdkVersion: {
                    type: 'array',
                    required: false,
                    default: () => getSdkVersion(),
                },
                onSuccessProp: {
                    type: 'function',
                    required: false,
                },
                onErrorProp: {
                    type: 'function',
                    required: false,
                },
                onOpenProp: {
                    type: 'function',
                    required: false,
                },
                onHideProp: {
                    type: 'function',
                    required: false,
                },
                onCloseProp: {
                    type: 'function',
                    required: false,
                },
                onEventProp: {
                    type: 'function',
                    required: false,
                },
            },
            attributes: {
                iframe: {
                    scrolling: 'no',
                    title: 'Pluggy',
                },
                popup: {
                    scrolling: 'no',
                    title: 'Pluggy',
                },
            },
            prerenderTemplate({ doc, props }) {
                const htmlNew = doc.createElement('html');
                htmlNew.innerHTML = loaderComponentHtml({
                    nonce: props.cspNonce,
                });
                return htmlNew;
            },
            containerTemplate({ doc, dimensions: { height, width }, close, uid, frame, prerenderFrame, event, props, }) {
                if (!prerenderFrame || !frame) {
                    throw new Error('Unexpected state: prerenderFrame or frame not defined');
                }
                // set container uid global reference
                pluggyConnectContainerUid = uid;
                const container = doc.createElement('div');
                const connectTheme = props.theme || 'light';
                container.id = uid;
                container.innerHTML = getModalWrapper(uid, connectTheme);
                const frameContainer = container.querySelector(`.${uid}_container`);
                if (!frameContainer) {
                    throw new Error('Unexpected state: not found frame container');
                }
                const visibleClassName = containerCssClassName('visible');
                const invisibleClassName = containerCssClassName('invisible');
                const prerenderFrameClassName = containerCssClassName('prerender-frame');
                frameContainer.appendChild(frame);
                frameContainer.appendChild(prerenderFrame);
                // Add styles
                const style = doc.createElement('style');
                // if allowFullscreen is not defined, default to true
                const allowFullscreenOrDefault = props.allowFullscreen !== undefined ? props.allowFullscreen : true;
                style.innerHTML = [
                    getCSS({
                        uid,
                        themeColor: 'fafafa',
                        height,
                        width,
                        allowFullscreen: allowFullscreenOrDefault,
                    }),
                    getContainerCss({ uid }),
                ].join('\n');
                container.appendChild(style);
                prerenderFrame.classList.add(prerenderFrameClassName);
                prerenderFrame.classList.add(visibleClassName);
                frame.classList.add(invisibleClassName);
                event.on(EVENT.RENDERED, () => {
                    // hide prerender frame, display content frame
                    prerenderFrame.classList.remove(visibleClassName);
                    prerenderFrame.classList.add(invisibleClassName);
                    frame.classList.remove(invisibleClassName);
                    frame.classList.add(visibleClassName);
                });
                // Remove scrolling from document body when modal is open (ie. visible)
                const modalVisibleClassName = containerCssClassName('has-modal-visible');
                event.on(EVENT.DISPLAY, () => {
                    // modal displayed, add visible class & call onOpen callback
                    document.body.classList.add(modalVisibleClassName);
                    const { onOpenProp } = props;
                    onOpenProp === null || onOpenProp === void 0 ? void 0 : onOpenProp();
                });
                // Wrap onCloseProp to be called only once, per component instance, which is
                // what we want to allow.
                // This "hack" is necessary because Zoid may trigger 2 times EVENT.CLOSE event,
                // for example when removing the container element from DOM and when explicitly
                // calling close() handler at the same time.
                // Each time a new container/component is rendered, the props are re-created from scratch,
                // so this will work for all cases.
                const onClosePropWrapper = props.onCloseProp
                    ? once(props.onCloseProp)
                    : null;
                event.on(EVENT.CLOSE, () => {
                    // modal closed, remove visible class & call onClose callback
                    // Note: this callback may be called 2 times in some cases due to Zoid logic,
                    // for example when unmounting container component AND calling close() explicitly.
                    document.body.classList.remove(modalVisibleClassName);
                    onClosePropWrapper === null || onClosePropWrapper === void 0 ? void 0 : onClosePropWrapper();
                });
                // receive message from iframe
                window.addEventListener('message', (event) => {
                    const { origin, data } = event;
                    const { url } = props;
                    const connectWebappUrl = url || 'https://connect.pluggy.ai';
                    if (origin !== connectWebappUrl) {
                        // origin not matching, comming from another place ignore message
                        return;
                    }
                    let type;
                    try {
                        ({ type } = JSON.parse(data));
                    }
                    catch (error) {
                        // could not parse, data is not a valid JSON string
                        return;
                    }
                    if (type !== 'CONTINUE_IN_BACKGROUND') {
                        // not a continueInBackground message, ignore
                        return;
                    }
                    // hide pluggy-connect, we remove the 'has-modal-visible' class to restore the scroll
                    document.body.classList.remove(modalVisibleClassName);
                }, false);
                // Register modal close() on 'Escape' key button press
                document.addEventListener('keydown', function escapeKeyCloseHandler(event) {
                    if (event.key !== 'Escape') {
                        return;
                    }
                    // escape key -> close (and remove 'keydown' listener)
                    close();
                    document.removeEventListener('keydown', escapeKeyCloseHandler);
                });
                return container;
            },
        });
    }
    return zoidComponentInstance;
}
export class PluggyConnect {
    constructor(props) {
        this.zoidComponent = initialize();
        // extend component props with current wrapper props
        const extendedProps = Object.assign(Object.assign({}, props), { sdkVersion: getSdkVersion() });
        // adapt props to avoid collision with Zoid component props
        // assign to private variable for reusability by React wrapper
        this.componentPropsExtendedAdapted = adaptPluggyConnectProps(extendedProps);
    }
    /**
     * Render the component using the specified component props,
     * as a modal with an iframe, appended to the page body (or the DOM uppermost element).
     *
     * @param containerElement - parent element where component should be rendered at. If not specified, will render at body root element.
     * @returns promise that resolves when rendered successfully, or throws if failed.
     */
    init(containerElement) {
        const container = containerElement ||
            (document.getElementsByTagName('body') ||
                document.getElementsByTagName('html') ||
                document.getElementsByTagName('div'))[0];
        // initialize zoid componentInstance
        this.componentInstance = this.zoidComponent(this.componentPropsExtendedAdapted);
        return this.componentInstance.render(container).catch((error) => {
            console.error(`Failed to render <PluggyConnect /> component`, error);
            throw error;
        });
    }
    /**
     * Manually cleanup the component.
     * This is not recommended, you should create the component once
     * and re-render it as needed each new time to save resources.
     */
    destroy() {
        zoidComponentInstance = undefined;
        return destroy();
    }
    /**
     * If the component is minimized, re-open it.
     * @retruns promise that resolves when re-opened successfully, or throws if failed.
     */
    show() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.componentInstance) {
                throw new Error('Failed to show <PluggyConnect /> component: component not initialized');
            }
            try {
                // open wrapper modal
                yield this.componentInstance.show();
                // add modal visible class
                const modalVisibleClassName = containerCssClassName('has-modal-visible');
                document.body.classList.add(modalVisibleClassName);
            }
            catch (error) {
                console.error(`Failed to show <PluggyConnect /> component`, error);
                throw error;
            }
        });
    }
    /**
     * Minimize the component.
     * Useful if you want to hide Connect widget after credentials have been submitted,
     * and you want to just continue connecting in background while still listening to
     * other callback events.
     */
    hide() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.componentInstance) {
                throw new Error('Failed to hide <PluggyConnect /> component: component not initialized');
            }
            try {
                // hide wrapper modal
                yield this.componentInstance.hide();
                // remove modal visible class
                const modalVisibleClassName = containerCssClassName('has-modal-visible');
                document.body.classList.remove(modalVisibleClassName);
            }
            catch (error) {
                console.error(`Failed to hide <PluggyConnect /> component`, error);
                throw error;
            }
        });
    }
}
export default PluggyConnect;
//# sourceMappingURL=data:application/json;base64,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